
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

interface SuiviPatient {
  id: string;
  objet: string;
  message: string;
  diagnostique?: string;
  traitement?: string;
  notes_medicales?: string;
  statut: string;
  date_creation: string;
  date_lecture?: string;
  patient: {
    nom: string;
    prenom: string;
    date_naissance: string;
  };
  medecin_expediteur: {
    nom: string;
    prenom: string;
    specialite: string;
  };
  medecin_destinataire: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

interface Prescription {
  id: string;
  medicaments: string;
  posologie?: string;
  duree_traitement?: string;
  instructions_particulieres?: string;
  photo_url?: string;
  date_creation: string;
  patient: {
    nom: string;
    prenom: string;
  };
  medecins: {
    nom: string;
    prenom: string;
    specialite: string;
  };
}

export default function AdminSuivisPage() {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const [suivis, setSuivis] = useState<SuiviPatient[]>([]);
  const [prescriptions, setPrescriptions] = useState<Prescription[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'suivis' | 'prescriptions'>('suivis');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSuivi, setSelectedSuivi] = useState<SuiviPatient | null>(null);
  const [selectedPrescription, setSelectedPrescription] = useState<Prescription | null>(null);
  const [showSuiviModal, setShowSuiviModal] = useState(false);
  const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }
    if (role !== 'super_admin') {
      navigate('/', { replace: true });
      return;
    }
    loadData();
  }, [user, role]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([loadSuivis(), loadPrescriptions()]);
    setLoading(false);
  };

  const loadSuivis = async () => {
    const { data, error } = await supabase
      .from('suivis_patients')
      .select(`
        *,
        patient:patients(nom, prenom, date_naissance),
        medecin_expediteur:medecins!medecin_expediteur_id(nom, prenom, specialite),
        medecin_destinataire:medecins!medecin_destinataire_id(nom, prenom, specialite)
      `)
      .order('date_creation', { ascending: false });

    if (!error && data) {
      setSuivis(data);
    }
  };

  const loadPrescriptions = async () => {
    const { data, error } = await supabase
      .from('prescriptions')
      .select(`
        *,
        patient:patients(nom, prenom),
        medecins(nom, prenom, specialite)
      `)
      .order('date_creation', { ascending: false });

    if (!error && data) {
      setPrescriptions(data);
    }
  };

  const filterSuivis = suivis.filter(suivi =>
    suivi.patient.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    suivi.patient.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    suivi.objet.toLowerCase().includes(searchTerm.toLowerCase()) ||
    suivi.medecin_expediteur.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    suivi.medecin_destinataire.nom.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filterPrescriptions = prescriptions.filter(prescription =>
    prescription.patient.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prescription.patient.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prescription.medicaments.toLowerCase().includes(searchTerm.toLowerCase()) ||
    prescription.medecins.nom.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSuiviStatutBadge = (statut: string) => {
    switch (statut) {
      case 'en_attente':
        return 'bg-yellow-100 text-yellow-700';
      case 'lu':
        return 'bg-blue-100 text-blue-700';
      case 'traite':
        return 'bg-green-100 text-green-700';
      case 'archive':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getSuiviStatutLabel = (statut: string) => {
    switch (statut) {
      case 'en_attente':
        return 'En attente';
      case 'lu':
        return 'Lu';
      case 'traite':
        return 'Traité';
      case 'archive':
        return 'Archivé';
      default:
        return statut;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Gestion des Suivis Médicaux</h1>
              <p className="mt-2 text-gray-600">
                Supervision des suivis de patients et prescriptions
              </p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Retour
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-2 flex gap-2">
            <button
              onClick={() => setActiveTab('suivis')}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'suivis'
                  ? 'bg-indigo-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-mail-line mr-2"></i>
              Suivis de Patients ({suivis.length})
            </button>
            <button
              onClick={() => setActiveTab('prescriptions')}
              className={`flex-1 px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
                activeTab === 'prescriptions'
                  ? 'bg-green-600 text-white shadow-md'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              <i className="ri-file-text-line mr-2"></i>
              Prescriptions ({prescriptions.length})
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
            <input
              type="text"
              placeholder={`Rechercher ${activeTab === 'suivis' ? 'un suivi' : 'une prescription'}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Content */}
        {activeTab === 'suivis' ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Suivis de Patients</h2>
              <p className="text-sm text-gray-600 mt-1">
                {filterSuivis.length} suivi(s) trouvé(s)
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {filterSuivis.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <i className="ri-mail-line text-5xl mb-4 text-gray-300"></i>
                  <p>Aucun suivi trouvé</p>
                </div>
              ) : (
                filterSuivis.map((suivi) => (
                  <div
                    key={suivi.id}
                    onClick={() => {
                      setSelectedSuivi(suivi);
                      setShowSuiviModal(true);
                    }}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900 text-lg">
                            {suivi.objet}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getSuiviStatutBadge(suivi.statut)}`}>
                            {getSuiviStatutLabel(suivi.statut)}
                          </span>
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                            <span>
                              <strong>Patient:</strong> {suivi.patient.nom} {suivi.patient.prenom}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-send-plane-line w-4 h-4 flex items-center justify-center"></i>
                            <span>
                              <strong>De:</strong> Dr. {suivi.medecin_expediteur.nom} {suivi.medecin_expediteur.prenom} ({suivi.medecin_expediteur.specialite})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-inbox-line w-4 h-4 flex items-center justify-center"></i>
                            <span>
                              <strong>À:</strong> Dr. {suivi.medecin_destinataire.nom} {suivi.medecin_destinataire.prenom} ({suivi.medecin_destinataire.specialite})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{formatDate(suivi.date_creation)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700 line-clamp-2">
                            {suivi.message}
                          </p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 text-xl ml-4"></i>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Prescriptions</h2>
              <p className="text-sm text-gray-600 mt-1">
                {filterPrescriptions.length} prescription(s) trouvée(s)
              </p>
            </div>
            <div className="divide-y divide-gray-200">
              {filterPrescriptions.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <i className="ri-file-text-line text-5xl mb-4 text-gray-300"></i>
                  <p>Aucune prescription trouvée</p>
                </div>
              ) : (
                filterPrescriptions.map((prescription) => (
                  <div
                    key={prescription.id}
                    onClick={() => {
                      setSelectedPrescription(prescription);
                      setShowPrescriptionModal(true);
                    }}
                    className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-semibold text-gray-900 text-lg">
                            Prescription médicale
                          </h3>
                          {prescription.photo_url && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <i className="ri-image-line mr-1"></i>
                              Photo
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-1 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                            <span>
                              <strong>Patient:</strong> {prescription.patient.nom} {prescription.patient.prenom}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-stethoscope-line w-4 h-4 flex items-center justify-center"></i>
                            <span>
                              <strong>Médecin:</strong> Dr. {prescription.medecins.nom} {prescription.medecins.prenom} ({prescription.medecins.specialite})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                            <span>{formatDate(prescription.date_creation)}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 bg-gray-50 rounded-lg p-3">
                          <p className="text-sm text-gray-700 line-clamp-2">
                            <strong>Médicaments:</strong> {prescription.medicaments}
                          </p>
                        </div>
                      </div>
                      <i className="ri-arrow-right-s-line text-gray-400 text-xl ml-4"></i>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Suivi Détail */}
      {showSuiviModal && selectedSuivi && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Détails du Suivi</h3>
              <button
                onClick={() => setShowSuiviModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                  <p className="text-gray-900">{selectedSuivi.patient.nom} {selectedSuivi.patient.prenom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <p className="text-gray-900">{formatDate(selectedSuivi.date_creation)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Médecin expéditeur</label>
                  <p className="text-gray-900">
                    Dr. {selectedSuivi.medecin_expediteur.nom} {selectedSuivi.medecin_expediteur.prenom}
                    <span className="block text-sm text-gray-600">{selectedSuivi.medecin_expediteur.specialite}</span>
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Médecin destinataire</label>
                  <p className="text-gray-900">
                    Dr. {selectedSuivi.medecin_destinataire.nom} {selectedSuivi.medecin_destinataire.prenom}
                    <span className="block text-sm text-gray-600">{selectedSuivi.medecin_destinataire.specialite}</span>
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Objet</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedSuivi.objet}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedSuivi.message}</p>
              </div>

              {selectedSuivi.diagnostique && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Diagnostic</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedSuivi.diagnostique}</p>
                </div>
              )}

              {selectedSuivi.traitement && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Traitement</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedSuivi.traitement}</p>
                </div>
              )}

              {selectedSuivi.notes_medicales && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes médicales</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedSuivi.notes_medicales}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Prescription Détail */}
      {showPrescriptionModal && selectedPrescription && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold">Détails de la Prescription</h3>
              <button
                onClick={() => setShowPrescriptionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient</label>
                  <p className="text-gray-900">{selectedPrescription.patient.nom} {selectedPrescription.patient.prenom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <p className="text-gray-900">{formatDate(selectedPrescription.date_creation)}</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Médecin prescripteur</label>
                <p className="text-gray-900">
                  Dr. {selectedPrescription.medecins.nom} {selectedPrescription.medecins.prenom}
                  <span className="block text-sm text-gray-600">{selectedPrescription.medecins.specialite}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Médicaments</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedPrescription.medicaments}</p>
              </div>

              {selectedPrescription.posologie && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Posologie</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedPrescription.posologie}</p>
                </div>
              )}

              {selectedPrescription.duree_traitement && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durée du traitement</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedPrescription.duree_traitement}</p>
                </div>
              )}

              {selectedPrescription.instructions_particulieres && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Instructions particulières</label>
                  <p className="text-gray-900 bg-gray-50 p-3 rounded-lg">{selectedPrescription.instructions_particulieres}</p>
                </div>
              )}

              {selectedPrescription.photo_url && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Photo de l'ordonnance</label>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <img 
                      src={selectedPrescription.photo_url} 
                      alt="Ordonnance" 
                      className="max-w-full h-auto rounded border cursor-pointer"
                      onClick={() => window.open(selectedPrescription.photo_url, '_blank')}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
